import * as React from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Field } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { keyFrom } from "@/lib/intake-flow";
import type { TreeSummary } from "@/lib/queries-intake-trees";

/**
 * Naming a new tree, or a copy of one (ACL-340).
 *
 * The key is derived from the title as a question's is, and is fixed once made: a run names the
 * tree it walked.
 */
export function TreeDialog({
  open,
  onOpenChange,
  source,
  taken,
  onSave,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The tree being copied, when this is a duplicate rather than a new one. */
  source?: TreeSummary | null;
  /** The keys already used, so a clash is caught before the server refuses it. */
  taken: string[];
  onSave: (next: { slug: string; title: string }) => void;
}) {
  const [title, setTitle] = React.useState("");

  React.useEffect(() => {
    if (!open) return;
    setTitle(source ? `${source.title} copy` : "");
  }, [open, source]);

  const slug = keyFrom(title, taken);
  // `keyFrom` already steps around the keys in use, so the key it returns is free.
  const ready = title.trim() !== "" && slug !== "";

  const submit = () => {
    if (!ready) return;
    onSave({ slug, title: title.trim() });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{source ? `Duplicate ${source.title}` : "New tree"}</DialogTitle>
          <DialogDescription>
            {source
              ? "A copy of its questions, as a draft. Nothing a rep is asked changes until you publish it and make it the one running."
              : "A set of questions. Every lead walks whichever tree is running."}
          </DialogDescription>
        </DialogHeader>

        <DialogBody className="space-y-4">
          <Field
            label="Name"
            htmlFor="tree-title"
            required
            hint={
              slug ? (
                <>
                  Its key will be <code className="font-mono">{slug}</code>, which never changes.
                </>
              ) : (
                "What this set of questions is called on the builder."
              )
            }
          >
            <Input
              id="tree-title"
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              placeholder="Texas, or Lopez Hurtado"
              autoFocus
              // One field, so Enter is the obvious way to finish.
              onKeyDown={(event) => event.key === "Enter" && submit()}
            />
          </Field>

        </DialogBody>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            disabled={!ready}
            onClick={submit}
          >
            {source ? "Duplicate" : "Make the tree"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
